 
// Note: The indent rule is disabled due to a known ESLint bug with complex JSX patterns
// that causes "Maximum call stack size exceeded" errors. The file is formatted with Prettier.
import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Legend,
  Cell,
  TooltipProps,
  Customized,
} from 'recharts';
import {
  ValueType,
  NameType,
} from 'recharts/types/component/DefaultTooltipContent';
import { ToasterNotice } from '../common/ToasterNotice';

// Types
export interface StepData {
  name: string; // Step label (e.g., "Exposure")
  time: number; // Duration in minutes
  displayTime: string; // Formatted (e.g., "2h 45m")
  color: string; // CSS variable reference or hex color
  teamMembers?: string[]; // Optional surgeon team members
}

export interface CaseStepBreakdownChartProps {
  data: StepData[];
  totalStepTime?: string; // Pre-formatted total (e.g., "6h 32m")
  onSegmentClick?: (step: StepData) => void;
}

// Minimum bar width for touch accessibility (44px)
const MIN_BAR_WIDTH = 44;

// Detect if device has touch capability
const isTouchDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    window.matchMedia('(pointer: coarse)').matches
  );
};

/**
 * CaseStepBreakdownChart - A mobile-first, adaptive vertical bar chart
 * for displaying surgical step efficiency data.
 */
export function CaseStepBreakdownChart({
  data,
  totalStepTime,
  onSegmentClick: _onSegmentClick,
}: CaseStepBreakdownChartProps): React.JSX.Element {
  const [selectedBar, setSelectedBar] = useState<StepData | null>(null);
  const [isTouch] = useState<boolean>(isTouchDevice);
  const [isScrolling, setIsScrolling] = useState<boolean>(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const chartLayoutRef = useRef<{
    axisX: number;
    barWidth: number;
    gap: number;
    chartLeft: number;
  } | null>(null);

  // Cleanup scroll timeout on unmount
  useEffect(() => {
    return (): void => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Handle scroll detection for mobile (disable selection during scroll)
  const handleScroll = useCallback(() => {
    if (isTouch) {
      setIsScrolling(true);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 150);
    }
  }, [isTouch]);

  // Calculate chart width based on data count and minimum bar width
  const calculateChartWidth = (): number => {
    const minWidth = data.length * (MIN_BAR_WIDTH + 16); // bar width + gap
    return Math.max(minWidth, 300);
  };

  // Get the maximum time value for Y-axis scale (in hours)
  const getMaxTimeHours = (): number => {
    if (data.length === 0) return 4;
    const maxMinutes = Math.max(...data.map((d) => d.time));
    const maxHours = maxMinutes / 60;
    // Round up to nearest 0.5 hour
    return Math.ceil(maxHours * 2) / 2;
  };

  // Generate Y-axis ticks in 30-minute (0.5 hour) increments
  const generateYAxisTicks = (): number[] => {
    const maxHours = getMaxTimeHours();
    const ticks: number[] = [];
    for (let i = 0; i <= maxHours; i += 0.5) {
      ticks.push(i * 60); // Convert to minutes for recharts
    }
    return ticks;
  };

  // Format Y-axis tick values (display in hours)
  const formatYAxisTick = (value: number): string => {
    const hours = value / 60;
    if (hours === 0) return '0h';
    if (hours === Math.floor(hours)) return `${hours}h`;
    return `${hours}`;
  };

  // Get the index of the selected bar for column highlighting
  const selectedBarIndex = selectedBar
    ? data.findIndex((d) => d.name === selectedBar.name)
    : -1;

  // Handle click on chart area to select column
  const handleChartClick = (e: React.MouseEvent): void => {
    if (isTouch && isScrolling) return;

    const layout = chartLayoutRef.current;
    if (!layout || data.length === 0) return;

    // Get click position relative to the chart container
    const container = scrollContainerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const clickX = e.clientX - rect.left + container.scrollLeft;

    // Calculate which column was clicked
    const { axisX, barWidth, gap, chartLeft } = layout;
    const relativeX = clickX - chartLeft;

    // Each column spans barWidth + gap, starting at axisX - gap/2
    const columnWidth = barWidth + gap;
    const startX = axisX - gap / 2 - chartLeft;

    if (relativeX < startX) return; // Click was before first column

    const columnIndex = Math.floor((relativeX - startX) / columnWidth);

    if (columnIndex >= 0 && columnIndex < data.length) {
      const clickedStep = data[columnIndex];
      // Toggle selection (highlights column only, no video action)
      if (selectedBar?.name === clickedStep.name) {
        setSelectedBar(null);
      } else {
        setSelectedBar(clickedStep);
      }
    }
  };

  // Custom tooltip for desktop hover
  const CustomTooltip = ({
    active,
    payload,
  }: TooltipProps<ValueType, NameType>): React.JSX.Element | null => {
    if (!active || !payload || payload.length === 0) return null;

    const stepData = payload[0].payload as StepData;

    return (
      <div
        className="rounded-[var(--radius)] border border-border p-3 shadow-elevation-sm"
        style={{
          backgroundColor: 'var(--popover)',
          color: 'var(--popover-foreground)',
        }}
      >
        <p className="font-semibold text-sm mb-1">{stepData.name}</p>
        <p className="text-sm font-semibold" style={{ color: stepData.color }}>
          {stepData.displayTime}
        </p>
        {stepData.teamMembers && stepData.teamMembers.length > 0 && (
          <div className="mt-2 pt-2 border-t border-border">
            <p className="text-xs text-low-emphasis-font">Team Members:</p>
            <p className="text-xs">{stepData.teamMembers.join(', ')}</p>
          </div>
        )}
      </div>
    );
  };

  // Custom legend renderer
  const renderLegend = (): React.JSX.Element => {
    return (
      <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mt-2">
        {data.map((step, index) => (
          <div key={index} className="flex items-center gap-1.5">
            <div
              className="w-3 h-3 rounded-sm flex-shrink-0"
              style={{ backgroundColor: step.color }}
            />
            <span className="text-xs text-medium-emphasis-font whitespace-nowrap">
              {step.name}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Determine if horizontal scroll is needed
  const needsScroll =
    calculateChartWidth() > (scrollContainerRef.current?.clientWidth || 0);
  const chartWidth = needsScroll ? calculateChartWidth() : '100%';

  return (
    <div
      className="rounded-[var(--radius-card)] border border-border p-4 flex flex-col"
      style={{ backgroundColor: 'var(--card)' }}
    >
      {/* Header Section - Responsive layout */}
      {/* Mobile: Title above, time/label stacked below */}
      {/* Desktop (md+): Title left, time/label right with label above time */}
      <div className="mb-4 flex flex-col md:flex-row md:justify-between md:items-start">
        <h3 className="text-lg font-semibold text-high-emphasis-font mb-2 md:mb-0">
          Case Step Breakdown
        </h3>
        {/* Mobile layout: time above label */}
        <div className="flex flex-col md:hidden">
          <span className="text-2xl font-bold text-high-emphasis-font">
            {totalStepTime || '0h 0m'}
          </span>
          <span className="text-sm text-medium-emphasis-font">
            Total Step Time
          </span>
        </div>
        {/* Desktop layout: label and time on same row, right-aligned */}
        <div className="hidden md:flex md:flex-row md:items-baseline md:gap-2">
          <span className="text-sm text-medium-emphasis-font">
            Total Step Time
          </span>
          <span className="text-2xl font-bold text-high-emphasis-font">
            {totalStepTime || '0h 0m'}
          </span>
        </div>
      </div>

      {/* Chart Container with horizontal scroll support */}
      <div
        ref={scrollContainerRef}
        className="overflow-x-auto overflow-y-hidden"
        onScroll={handleScroll}
        onClick={handleChartClick}
        style={{ cursor: needsScroll ? 'grab' : 'default' }}
      >
        <div style={{ width: chartWidth, minWidth: '100%', height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 20, right: 20, left: 10, bottom: 5 }}
              barCategoryGap={isTouch ? 8 : 16}
            >
              <CartesianGrid
                strokeDasharray="0"
                vertical={false}
                stroke="var(--border)"
                strokeWidth={1}
              />

              {/* X-Axis: Labels hidden since legend provides step names */}
              <XAxis
                dataKey="name"
                tick={false}
                axisLine={{ stroke: 'var(--border)' }}
                tickLine={false}
                height={10}
              />

              {/* Y-Axis: 30-minute increments */}
              <YAxis
                ticks={generateYAxisTicks()}
                tickFormatter={formatYAxisTick}
                domain={[0, getMaxTimeHours() * 60]}
                axisLine={{ stroke: 'var(--border)' }}
                tickLine={false}
                tick={{ fontSize: 11, fill: 'var(--medium-emphasis-font)' }}
                width={35}
              />

              {/* Tooltip: Only shown on desktop hover */}
              {!isTouch && (
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{ fill: 'var(--surface-200)', fillOpacity: 0.3 }}
                />
              )}

              {/* Hide default legend - we render custom legend */}
              <Legend content={() => null} />

              {/* Custom vertical lines between bars and column highlighting */}
              <Customized
                component={(props: {
                  xAxisMap?: Record<string, { x: number; width: number; bandSize: number }>;
                  offset?: { top: number; bottom: number; left: number; right: number };
                  height?: number;
                }) => {
                  const { xAxisMap, offset, height } = props;
                  if (!xAxisMap || !offset || !height || data.length < 1) return null;

                  const xAxis = Object.values(xAxisMap)[0];
                  if (!xAxis) return null;

                  const { x: axisX, width: axisWidth, bandSize } = xAxis;
                  const chartTop = offset.top;
                  const chartBottom = height - offset.bottom;
                  const chartHeight = chartBottom - chartTop;

                  const elements = [];
                  const barWidth = bandSize;
                  const totalWidth = axisWidth;
                  const gap = (totalWidth - barWidth * data.length) / data.length;

                  // Store layout for click handling
                  chartLayoutRef.current = {
                    axisX,
                    barWidth,
                    gap,
                    chartLeft: offset.left,
                  };

                  // Add column highlight for selected bar
                  if (selectedBarIndex >= 0) {
                    const columnX = axisX + (barWidth + gap) * selectedBarIndex - gap / 2;
                    const columnWidth = barWidth + gap;

                    elements.push(
                      <rect
                        key="column-highlight"
                        x={columnX}
                        y={chartTop}
                        width={columnWidth}
                        height={chartHeight}
                        fill="var(--surface-400)"
                      />
                    );
                  }

                  // Add vertical lines between bars
                  if (data.length >= 2) {
                    for (let i = 1; i < data.length; i++) {
                      const lineX = axisX + (barWidth + gap) * i - gap / 2;

                      elements.push(
                        <line
                          key={`separator-${i}`}
                          x1={lineX}
                          y1={chartTop}
                          x2={lineX}
                          y2={chartBottom}
                          stroke="var(--border)"
                          strokeWidth={1}
                        />
                      );
                    }

                    // Add right border line after the last bar
                    const rightLineX = axisX + totalWidth;
                    elements.push(
                      <line
                        key="separator-right"
                        x1={rightLineX}
                        y1={chartTop}
                        x2={rightLineX}
                        y2={chartBottom}
                        stroke="var(--border)"
                        strokeWidth={1}
                      />
                    );
                  }

                  return <g>{elements}</g>;
                }}
              />

              {/* Bar component */}
              <Bar
                dataKey="time"
                radius={[4, 4, 0, 0]}
                minPointSize={2}
                style={{ cursor: 'pointer' }}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                    stroke={
                      selectedBar?.name === entry.name
                        ? 'var(--bar-stroke)'
                        : 'none'
                    }
                    strokeWidth={selectedBar?.name === entry.name ? 2 : 0}
                    style={{ cursor: 'pointer' }}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Legend - Always visible */}
      {renderLegend()}

      {/* Toaster Notice (100% width, anchored to bottom) */}
      {isTouch && selectedBar && (
        <ToasterNotice
          title={selectedBar.name}
          content={
            <span className="text-sm" style={{ color: selectedBar.color }}>
              {selectedBar.displayTime}
            </span>
          }
          onClose={() => setSelectedBar(null)}
        />
      )}
    </div>
  );
}

export default CaseStepBreakdownChart;
